import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSnackbar } from 'notistack';
import useRequest from 'hooks/useRequest';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { ParametersContext } from 'components/ParametersGuard';
import { UserContext } from 'components/UserGuard';
import { Box, Tab, Tabs } from '@mui/material';
import ClaimsService from 'services/api/ClaimsService';
import ApplicationsService from 'services/api/ApplicationsService';
import ActiveClaimsTab from './ActiveClaimsTab';
import HistoricClaimsTab from './HistoricClaimsTab';
import ReviewClaimsTab from './ReviewClaimsTab';
import DeleteDraftApplicationModal from './DeleteDraftApplicationModal';
import DisputeClaimModal from './DisputeClaimModal';
import UploadClaimFilesModal from './UploadClaimFilesModal';
import Spinner from 'components/shared/Spinner';
import CompanySelect from 'components/company/CompanySelect';
import routes, { claimsRoutes } from 'store/configs/Routes';
import { Claim } from 'store/types/Claim';
import ClaimStatus from 'store/enums/ClaimStatus';
import ClaimType from 'store/enums/ClaimType';
import ClaimUploadType from 'store/enums/ClaimUploadType';
import Application from 'store/types/Application';
import SiteModule from 'store/enums/SiteModule';
import ApplicationType from 'store/types/ApplicationType';
import { getActiveTabRoute, getActiveTabValue } from 'util/Route';
import { defaultSnackbarErrorProps } from 'util/Layout';
import { CompanySelectOption, getCompanySelectOptions } from 'util/Company';

import commonStyles from 'styles/common.module.scss';

enum ClaimsTab {
  Active = '1',
  Historic = '2',
  Judgement = '3',
}

const isClaimClosed = (c: Claim) =>
  (c.type === ClaimType.Claim && c.claimStatus === ClaimStatus.Closed) ||
  (c.type === ClaimType.Dispute && c.disputeStatus === ClaimStatus.Closed);

const ClaimsPage: React.FunctionComponent = () => {
  const { replace, push } = useHistory();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { functions = [], userLoading, userPermissions } = useContext(UserContext);
  const [selectedCompany, setSelectedCompany] = useState<CompanySelectOption | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim>();
  const [uploadType, setUploadType] = useState<ClaimUploadType>(ClaimUploadType.Files);

  const {
    claims: { claimsPageHeader, historicClaimsPageHeader, underReviewPageHeader },
  } = useContext(ParametersContext);
  const [submitLoading, setSubmitLoading] = useState<boolean>();

  const activeTab: string = useMemo(() => getActiveTabValue(pathname, claimsRoutes), [pathname]);

  const handleTabChange = useCallback(
    (_: any, value = ClaimsTab.Active) => {
      replace(getActiveTabRoute(value, claimsRoutes));
    },
    [replace]
  );

  const permissions: string[] = useMemo(
    () => userPermissions.modules.find(({ name }) => name === SiteModule.Company)?.permissions || [],
    [userPermissions]
  );
  const options: CompanySelectOption[] = useMemo(
    () => getCompanySelectOptions(functions, permissions),
    [functions, permissions]
  );

  const claimsRequest = useMemo(
    () => (selectedCompany?.id ? () => ClaimsService.getClaims(selectedCompany.id) : undefined),
    [selectedCompany]
  );
  const { data = [], loading, refetch } = useRequest<Claim[]>(claimsRequest);

  const applicationTypesRequest = useMemo(
    () => (selectedCompany?.id ? () => ApplicationsService.getApplicationTypes(selectedCompany.id) : undefined),
    [selectedCompany?.id]
  );

  const { data: applicationTypes, loading: applicationTypesLoading } =
    useRequest<ApplicationType[]>(applicationTypesRequest);

  const myClaims = useMemo(
    () => data?.filter((c) => c.debtorCompanyId !== selectedCompany?.id && !isClaimClosed(c)),
    [data, selectedCompany?.id]
  );
  const claimsAgainstMe = useMemo(
    () => data?.filter((c) => c.debtorCompanyId === selectedCompany?.id && !isClaimClosed(c)),
    [data, selectedCompany?.id]
  );
  const historicClaims = useMemo(() => data?.filter((c) => isClaimClosed(c)), [data]);
  const underReviewClaims = useMemo(() => data?.filter((c) => c.type === ClaimType.Dispute && c.isJudge), [data]);

  const handleCompanyChange = useCallback(
    (newCompanyId: string) => {
      setSelectedCompany(options.find(({ id }) => id === newCompanyId) || undefined);
    },
    [options]
  );

  const handleCreateApplication = useCallback(
    (applicationId: string) => {
      if (!applicationId) return;

      setSubmitLoading(true);
      ApplicationsService.createApplication(applicationId, undefined, selectedCompany?.id)
        .then(({ id }: Application) => {
          setSubmitLoading(false);
          enqueueSnackbar('Draft application successfully created', { variant: 'success' });
          push(`${routes.editApplication}/${id}`);
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [enqueueSnackbar, push, selectedCompany?.id]
  );

  const handleDeleteModalOpen = useCallback((claim: Claim) => {
    setSelectedClaim(claim);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setDeleteModalOpen(false);
  }, []);

  const handleDeleteModalSubmit = useCallback(() => {
    setSelectedClaim(undefined);
    refetch();
    setDeleteModalOpen(false);
  }, [refetch]);

  const handleDisputeModalOpen = useCallback((claim: Claim) => {
    setSelectedClaim(claim);
    setDisputeModalOpen(true);
  }, []);

  const handleDisputeModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setDisputeModalOpen(false);
  }, []);

  const handleDisputeModalSubmit = useCallback(() => {
    setSelectedClaim(undefined);
    refetch();
    setDisputeModalOpen(false);
  }, [refetch]);

  const handleFileUploadModalOpen = useCallback((claim: Claim, uploadType: ClaimUploadType) => {
    setSelectedClaim(claim);
    setUploadType(uploadType);
    setFileUploadModalOpen(true);
  }, []);

  const handleFileUploadModalClose = useCallback(() => {
    setSelectedClaim(undefined);
    setUploadType(ClaimUploadType.Files);
    setFileUploadModalOpen(false);
  }, []);

  return (
    <Spinner loading={userLoading || applicationTypesLoading || submitLoading} fullPage={true}>
      <h1 className={commonStyles.pageTitle}>My Claims & Disputes</h1>
      <Box sx={{ mb: 4 }}>
        {claimsPageHeader && activeTab === ClaimsTab.Active && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: claimsPageHeader }} />
        )}
        {historicClaimsPageHeader && activeTab === ClaimsTab.Historic && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: historicClaimsPageHeader }} />
        )}
        {underReviewPageHeader && activeTab === ClaimsTab.Judgement && (
          <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: underReviewPageHeader }} />
        )}

        <CompanySelect
          value={selectedCompany?.id}
          options={options}
          disabled={loading}
          onChange={handleCompanyChange}
          size={'small'}
        />
      </Box>
      <TabContext value={activeTab}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor={'primary'}
          textColor={'primary'}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
          }}
        >
          <Tab
            value={ClaimsTab.Active}
            label={'Active'}
            className={commonStyles.tab}
            classes={{ selected: commonStyles.activeTab }}
          />
          <Tab
            value={ClaimsTab.Historic}
            label={'Historic'}
            className={commonStyles.tab}
            classes={{ selected: commonStyles.activeTab }}
          />
          <Tab
            value={ClaimsTab.Judgement}
            label={'Judges Review'}
            className={commonStyles.tab}
            classes={{ selected: commonStyles.activeTab }}
          />
        </Tabs>
        <TabPanel value={ClaimsTab.Active} className={commonStyles.tabPanel}>
          <ActiveClaimsTab
            myClaims={myClaims}
            claimsAgainstMe={claimsAgainstMe}
            applicationTypes={applicationTypes}
            loading={loading}
            onApplicationCreateClick={handleCreateApplication}
            onApplicationDeleteClick={handleDeleteModalOpen}
            onDisputeClaimClick={handleDisputeModalOpen}
            onFileUploadClick={handleFileUploadModalOpen}
          />
        </TabPanel>
        <TabPanel value={ClaimsTab.Historic} className={commonStyles.tabPanel}>
          <HistoricClaimsTab historicClaims={historicClaims} companyId={selectedCompany?.id} loading={loading} />
        </TabPanel>
        <TabPanel value={ClaimsTab.Judgement} className={commonStyles.tabPanel}>
          <ReviewClaimsTab underReviewClaims={underReviewClaims} companyId={selectedCompany?.id} loading={loading} />
        </TabPanel>
      </TabContext>
      {selectedCompany && (
        <>
          <DeleteDraftApplicationModal
            data={selectedClaim}
            open={deleteModalOpen}
            onClose={handleDeleteModalClose}
            onSubmit={handleDeleteModalSubmit}
          />
          <UploadClaimFilesModal
            companyId={selectedCompany?.id}
            data={selectedClaim}
            uploadType={uploadType}
            open={fileUploadModalOpen}
            onClose={handleFileUploadModalClose}
            onSubmit={handleFileUploadModalClose}
          />
          <DisputeClaimModal
            companyId={selectedCompany?.id}
            data={selectedClaim}
            open={disputeModalOpen}
            onClose={handleDisputeModalClose}
            onSubmit={handleDisputeModalSubmit}
          />
        </>
      )}
    </Spinner>
  );
};

export default ClaimsPage;
